import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import AdminSetup from './AdminSetup';

const CreativeLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkIfSetupNeeded();
  }, []);

  const checkIfSetupNeeded = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/check-setup`);
      const data = await response.json();
      setNeedsSetup(data.needs_setup);
    } catch (error) {
      console.error('Error checking setup status:', error);
    } finally {
      setCheckingSetup(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('creative_token', data.token);
        toast.success('Welcome back!');
        onLogin(data.token);
        navigate('/creative-dashboard');
      } else {
        toast.error('Login failed', {
          description: data.error || 'Invalid email or password',
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed', {
        description: 'Unable to connect to server',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_creative-studio-146/artifacts/mfvy9mdh_sanjuanventures_A_3D_illustration_showing_four_people_two_men_55abb2cb-5ab1-4953-99f9-38c0fd640c4a_3.png"
            alt="Background"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/10"></div>
        </div>
        
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-900 font-medium drop-shadow-md">Loading...</p>
        </div>
      </div>
    );
  }

  if (needsSetup) {
    return <AdminSetup onSetupComplete={() => setNeedsSetup(false)} />;
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center p-6">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://customer-assets.emergentagent.com/job_creative-studio-146/artifacts/mfvy9mdh_sanjuanventures_A_3D_illustration_showing_four_people_two_men_55abb2cb-5ab1-4953-99f9-38c0fd640c4a_3.png"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      {/* Back to Site Button */}
      <a
        href="/"
        className="absolute top-6 left-6 z-20 text-white hover:text-pink-400 transition-colors flex items-center gap-1 text-sm font-medium drop-shadow-lg"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back
      </a>
      
      <div className="relative z-10 w-full max-w-md">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-lg">
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              required
              value={credentials.email}
              onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              placeholder="admin@lettherebe.ai"
              className="w-full placeholder:text-pink-400"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-lg">
              Password
            </label>
            <Input
              id="password"
              type="password"
              required
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              placeholder="Enter your password"
              className="w-full placeholder:text-pink-400"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold py-6 text-lg hover:shadow-lg transition-all duration-300"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreativeLogin;